/**
 * NavList component for the Indigo design system.
 *
 * A vertical list for master–detail layouts: pick an item on the left, see its
 * detail on the right. Items can be grouped under a label, carry an optional
 * status dot, and the selected item is lifted onto a card.
 *
 * Composition: `NavList` › `NavListGroup` (optional) › `NavListItem`.
 *
 * @example
 * ```tsx
 * <NavList aria-label="Messages">
 *   <NavListGroup label="Jane Smith" description="(Applicant attorney)">
 *     <NavListItem
 *       title="New thread"
 *       description="Standard message"
 *       indicator="info"
 *       selected={selectedId === "jane-new"}
 *       onClick={() => setSelectedId("jane-new")}
 *     />
 *   </NavListGroup>
 * </NavList>
 * ```
 */
import { clsx } from "clsx";
import { type MouseEventHandler, type ReactNode, useId } from "react";
import type { StatusChipDotVariant } from "../StatusChip";
import "./NavList.css";

export interface NavListProps {
  /** Accessible name for the navigation region. */
  "aria-label": string;

  /** `NavListGroup`s and/or `NavListItem`s. */
  children: ReactNode;

  /** Additional CSS classes applied to the root element. */
  className?: string;

  /** Test id applied to the root element. */
  "data-testid"?: string;
}

export const NavList = ({
  "aria-label": ariaLabel,
  children,
  className,
  "data-testid": dataTestId,
}: NavListProps) => (
  <nav
    aria-label={ariaLabel}
    className={clsx("indigo-nav-list", className)}
    data-testid={dataTestId}
  >
    <ul className="indigo-nav-list__items">{children}</ul>
  </nav>
);

NavList.displayName = "NavList";

export interface NavListGroupProps {
  /** Group label. */
  label: ReactNode;

  /** Quieter text after the label (e.g. a role). */
  description?: ReactNode;

  /** `NavListItem`s in the group. */
  children: ReactNode;

  /** Additional CSS classes applied to the group. */
  className?: string;
}

export const NavListGroup = ({
  label,
  description,
  children,
  className,
}: NavListGroupProps) => {
  const labelId = useId();

  return (
    <li className={clsx("indigo-nav-list__group", className)}>
      <div id={labelId} className="indigo-nav-list__group-label">
        <span className="indigo-nav-list__group-title">{label}</span>
        {description && (
          <span className="indigo-nav-list__group-description">
            {description}
          </span>
        )}
      </div>
      <ul className="indigo-nav-list__items" aria-labelledby={labelId}>
        {children}
      </ul>
    </li>
  );
};

NavListGroup.displayName = "NavListGroup";

export interface NavListItemProps {
  /** Item title. */
  title: ReactNode;

  /** Secondary line under the title. */
  description?: ReactNode;

  /**
   * Status dot before the title. Uses the same palette as `StatusChip`'s dot
   * mode, so a status reads the same here as everywhere else.
   */
  indicator?: StatusChipDotVariant;

  /** Marks the item as the one whose detail is shown. */
  selected?: boolean;

  /** Selects the item. */
  onClick?: MouseEventHandler<HTMLButtonElement>;

  /** Temporarily blocks selection, e.g. while a step is saving. */
  disabled?: boolean;

  /** Additional CSS classes applied to the item. */
  className?: string;

  /** Test id applied to the item's button. */
  "data-testid"?: string;
}

export const NavListItem = ({
  title,
  description,
  indicator,
  selected = false,
  onClick,
  disabled = false,
  className,
  "data-testid": dataTestId,
}: NavListItemProps) => (
  <li className={clsx("indigo-nav-list__item", className)}>
    <button
      type="button"
      className={clsx(
        "indigo-nav-list__button",
        indicator && "indigo-nav-list__button--with-indicator",
        selected && "indigo-nav-list__button--selected",
      )}
      aria-current={selected ? "true" : undefined}
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {indicator && (
        <span
          className={clsx(
            "indigo-nav-list__indicator",
            `indigo-nav-list__indicator--${indicator}`,
          )}
          aria-hidden="true"
        />
      )}
      <span className="indigo-nav-list__title">{title}</span>
      {description && (
        <span className="indigo-nav-list__description">{description}</span>
      )}
    </button>
  </li>
);

NavListItem.displayName = "NavListItem";
